import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { ReportTable } from '@/components/ReportTable';
import { apiFetch } from '@/lib/api';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface StockRow {
  id: number;
  code: string;
  name: string;
  unit: string;
  stock_qty: number;
  min_stock: number;
  low_stock: number;
  selling_price: number;
}

interface StockData {
  rows: StockRow[];
}

interface StockResponse {
  data: StockData;
}

// ─── Columns ───────────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'code', header: 'Code' },
  { key: 'name', header: 'Name' },
  { key: 'unit', header: 'Unit' },
  { key: 'stock_qty', header: 'Stock Qty' },
  { key: 'min_stock', header: 'Min Stock' },
  { key: 'low_stock', header: 'Low Stock' },
  { key: 'selling_price', header: 'Selling Price', money: true },
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function StockReport() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['report', 'stock'],
    queryFn: () => apiFetch<StockResponse>('/api/reports/stock'),
  });

  const report = data?.data;
  const rows = (report?.rows ?? []).map((r) => ({
    ...r,
    low_stock: r.low_stock ? 'Low' : '',
  })) as Record<string, unknown>[];

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        <ChevronLeft className="h-3 w-3" />
        Reports
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Stock Report</h1>
        <ExportButtons endpoint="/api/reports/stock" />
      </div>

      {/* Loading / error */}
      {isLoading && (
        <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>
      )}
      {isError && (
        <p className="text-sm text-destructive py-10 text-center">
          {error instanceof Error ? error.message : 'Failed to load report'}
        </p>
      )}

      {report && <ReportTable columns={COLUMNS} rows={rows} keyField="id" />}
    </div>
  );
}
